import React, { useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Divider } from 'primereact/divider'
import { TabView, TabPanel } from 'primereact/tabview'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Badge } from 'primereact/badge'
import { Chip } from 'primereact/chip'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Skeleton } from 'primereact/skeleton'
import { useBulkOrderAssignment } from '../../../hooks/useBulkOrderAssignment'
import { useOrderData } from '../../../hooks/useOrderData'
import { useLineItems } from '../../../hooks/useLineItems'
import { ViewOrderInfoDialog } from './../ViewOrderInfoDialog'
import { useOrderSelection } from '../../../hooks/useOrderSelection'
import { useLineItemSelection } from '../../../hooks/useLineItemSelection'

interface ExecutionSuccessDialogProps {
  useBulkOrderAssignment: ReturnType<typeof useBulkOrderAssignment>
  orderData: ReturnType<typeof useOrderData>
  orderSelection: ReturnType<typeof useOrderSelection>
  lineItemsHook: ReturnType<typeof useLineItems>
  lineItemSelection: ReturnType<typeof useLineItemSelection>
  onCloseDialog: () => void
}

export const ExecutionSuccessDialog: React.FC<ExecutionSuccessDialogProps> = ({
  useBulkOrderAssignment,
  orderSelection,
  orderData,
  lineItemsHook,
  lineItemSelection,
  onCloseDialog
}) => {
  const { executionResults, isSubmitting, toast } = useBulkOrderAssignment
  const [isOrderDialogVisible, setIsOrderDialogVisible] = useState(false)

  // Show loading state when submitting but no results yet
  const isLoading = isSubmitting && !executionResults

  // Handler for viewing order details
  const handleViewOrderDetails = (orderFromResults: any) => {
    // Find the full order object from the main orders array using order_id
    const fullOrder = orderData.orders.find(o => o.order_id === orderFromResults.order_id)
    
    if (fullOrder) {
      orderSelection.selectOrder(fullOrder)
      setIsOrderDialogVisible(true)
    } else {
      // Show toast error if order not found
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Order details not found. Please refresh and try again.',
        life: 3000
      })
    }
  }

  // Actions template for DataTable
  const actionsTemplate = (rowData: any) => (
    <Button
      label="Details"
      icon="pi pi-eye"
      size="small"
      className="p-button-outlined p-button-sm"
      onClick={() => handleViewOrderDetails(rowData)}
    />
  )

  // Loading skeleton for data tables
  const renderSkeletonTable = () => (
    <div className="p-3">
      {Array.from({ length: 1 }).map((_, index) => (
        <div key={index} className="flex align-items-center gap-3 mb-3 p-2">
          <Skeleton width="3rem" height="1.5rem" />
          <Skeleton width="12rem" height="2rem" />
          <Skeleton width="4rem" height="1.5rem" />
          <Skeleton width="8rem" height="1.5rem" />
          <Skeleton width="6rem" height="1.5rem" />
          <Skeleton width="4rem" height="2rem" />
        </div>
      ))}
    </div>
  )

  return (
    <>
      {/* Hero Message - Loading or Success */}
      <div className="mb-4">
        {isLoading ? (
          <Card 
            className="shadow-2 border-round-xl" 
            style={{ 
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              border: 'none'
            }}
          >
            <div className="text-white p-4 text-center">
              <div className="mb-3">
                <ProgressSpinner 
                  style={{ width: '3rem', height: '3rem' }} 
                  strokeWidth="4" 
                  fill="transparent"
                />
              </div>
              <h3 className="m-0 mb-2 text-white font-bold text-2xl">
                Assigning Orders...
              </h3>
              <div className="text-lg opacity-90 mb-2">
                Processing line item assignments to stores
              </div>
              <div className="text-sm opacity-75">
                Please wait while we complete the bulk assignment process
              </div>
            </div>
          </Card>
        ) : (
          <Card 
            className="shadow-2 border-round-xl" 
            style={{ 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none'
            }}
          >
            <div className="text-white p-4 text-center">
              <div className="mb-3">
                <i className="pi pi-check-circle" style={{ fontSize: '3rem' }}></i>
              </div>
              <h3 className="m-0 mb-2 text-white font-bold text-2xl">
                Assignment Complete!
              </h3>
              <div className="text-lg opacity-90">
                Bulk order assignment has been processed successfully
              </div>
            </div>
          </Card>
        )}
      </div>

      <Divider />

      {/* Execution Summary - Loading or Results */}
      <div className="mb-4">
        <h6 className="mb-3 flex align-items-center gap-2">
          <i className="pi pi-chart-bar text-primary" style={{ fontSize: '1.1rem', marginRight: '8px'}}></i>
                  {isLoading ? 'Processing Summary' : 'Execution Summary'}
      </h6>
        
        {isLoading ? (
          <div 
            className="flex gap-3" 
            style={{ 
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'stretch',
              flexWrap: 'nowrap',
              width: '100%'
            }}
          >
            <Card 
              style={{ 
                flex: '1',
                maxWidth: '200px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                borderRadius: '12px',
                color: 'white',
                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                border: 'none',
                padding: '0'
              }} 
              className="p-0"
            >
              <div style={{ padding: '18px', textAlign: 'center' }}>
                <div style={{ marginBottom: '10px' }}>
                  <i className="pi pi-shopping-cart" style={{ fontSize: '1.6rem' }}></i>
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>
                  <Skeleton width="3rem" height="1.8rem" style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
                </div>
                <div style={{ fontSize: '0.95rem', opacity: '0.9' }}>
                  Orders Processed
                </div>
              </div>
            </Card>

            <Card 
              style={{ 
                flex: '1',
                maxWidth: '200px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '12px',
                color: 'white',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                border: 'none',
                padding: '0'
              }} 
              className="p-0"
            >
              <div style={{ padding: '18px', textAlign: 'center' }}>
                <div style={{ marginBottom: '10px' }}>
                  <i className="pi pi-list" style={{ fontSize: '1.6rem' }}></i>
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>
                  <Skeleton width="3rem" height="1.8rem" style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
                </div>
                <div style={{ fontSize: '0.95rem', opacity: '0.9' }}>
                  Line Items Assigned
                </div>
              </div>
            </Card>

            <Card 
              style={{ 
                flex: '1',
                maxWidth: '200px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: '12px',
                color: 'white',
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)',
                border: 'none',
                padding: '0'
              }} 
              className="p-0"
            >
              <div style={{ padding: '18px', textAlign: 'center' }}>
                <div style={{ marginBottom: '10px' }}>
                  <i className="pi pi-percentage" style={{ fontSize: '1.6rem' }}></i>
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>
                  <Skeleton width="3rem" height="1.8rem" style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
                </div>
                <div style={{ fontSize: '0.95rem', opacity: '0.9' }}>
                  Order Success Rate
                </div>
              </div>
            </Card>
          </div>
        ) : executionResults ? (
          <div 
            className="flex gap-3" 
            style={{ 
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'stretch',
              flexWrap: 'nowrap',
              width: '100%'
            }}
          >
            <Card 
              style={{ 
                flex: '1',
                maxWidth: '200px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                borderRadius: '12px',
                color: 'white',
                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                border: 'none',
                padding: '0'
              }} 
              className="p-0"
            >
              <div style={{ padding: '18px', textAlign: 'center' }}>
                <div style={{ marginBottom: '10px' }}>
                  <i className="pi pi-shopping-cart" style={{ fontSize: '1.6rem' }}></i>
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '6px' }}>
                  {executionResults.execution_summary.total_orders_processed}
                </div>
                <div style={{ fontSize: '0.95rem', opacity: '0.9' }}>
                  Orders Processed
                </div>
              </div>
            </Card>

            <Card 
              style={{ 
                flex: '1',
                maxWidth: '200px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '12px',
                color: 'white',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                border: 'none',
                padding: '0'
              }} 
              className="p-0"
            >
              <div style={{ padding: '18px', textAlign: 'center' }}>
                <div style={{ marginBottom: '10px' }}>
                  <i className="pi pi-list" style={{ fontSize: '1.6rem' }}></i>
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '6px' }}>
                  {executionResults.execution_summary.total_line_items_processed}
                </div>
                <div style={{ fontSize: '0.95rem', opacity: '0.9' }}>
                  Line Items Assigned
                </div>
              </div>
            </Card>

            <Card 
              style={{ 
                flex: '1',
                maxWidth: '200px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: '12px',
                color: 'white',
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)',
                border: 'none',
                padding: '0'
              }} 
              className="p-0"
            >
              <div style={{ padding: '18px', textAlign: 'center' }}>
                <div style={{ marginBottom: '10px' }}>
                  <i className="pi pi-percentage" style={{ fontSize: '1.6rem' }}></i>
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '6px' }}>
                  {executionResults.execution_summary.success_rate.orders}
                </div>
                <div style={{ fontSize: '0.95rem', opacity: '0.9' }}>
                  Order Success Rate
                </div>
              </div>
            </Card>
          </div>
        ) : null}
      </div>

      <Divider />

      {/* Detailed Results - Loading or Data */}
      <div className="mb-4">
        <h6 className="mb-3 flex align-items-center gap-2">
          <i className="pi pi-list text-primary" style={{ fontSize: '1.1rem', marginRight: '8px'}}></i>
          {isLoading ? 'Processing Order Details...' : 'Order Details'}
        </h6>
        
        {isLoading ? (
          <TabView>
            <TabPanel 
              header={
                <div className="flex align-items-center">
                  <ProgressSpinner 
                    style={{ width: '1rem', height: '1rem', marginRight: '8px' }} 
                    strokeWidth="4"
                  />
                  <span>Processing...</span>
                </div>
              }
            >
              {renderSkeletonTable()}
            </TabPanel>
          </TabView>
        ) : executionResults ? (
          <TabView>
            {/* Successful Orders Tab */}
            <TabPanel 
              header={
                <div className="flex align-items-center">
                  <i className="pi pi-check-circle text-green-600 mr-2" style={{marginRight: '8px'}}></i>
                  <span>Successful</span>
                  <Badge value={executionResults.detailed_results.successful_orders.length} className="ml-2" severity="success" style={{marginLeft: '8px'}}/>
                </div>
              }
            >
              <DataTable 
                value={executionResults.detailed_results.successful_orders}
                size="small"
                stripedRows
                showGridlines
                className="p-datatable-sm"
                paginator={true}
                rows={10}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} orders"
              >
                <Column 
                  field="order_name" 
                  header="Order" 
                  body={(rowData) => (
                    <div>
                      <div className="font-semibold">{rowData.order_name}</div>
                      <div className="text-sm text-600">ID: {rowData.order_id}</div>
                    </div>
                  )}
                  style={{ minWidth: '200px' }}
                />
                <Column 
                  field="fulfillment_type" 
                  header="Type" 
                  body={(rowData) => (
                    <Chip 
                      label={rowData.fulfillment_type === 'full' ? 'Full' : 'Partial'} 
                      className={rowData.fulfillment_type === 'full' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}
                      style={{ fontSize: '0.75rem' }}
                    />
                  )}
                  style={{ width: '120px' }}
                />
                <Column 
                  field="total_line_items" 
                  header="Items" 
                  style={{ width: '80px' }}
                />
                <Column 
                  field="assigned_stores" 
                  header="Stores" 
                  body={(rowData) => (
                    <div className="text-sm">
                      {rowData.assigned_stores.join(', ')}
                    </div>
                  )}
                  style={{ minWidth: '150px' }}
                />
                <Column 
                  field="order_status" 
                  header="Status" 
                  body={(rowData) => (
                    <Badge value={rowData.order_status} severity="success" />
                  )}
                  style={{ width: '120px' }}
                />
                <Column 
                  header="Actions" 
                  body={actionsTemplate}
                  style={{ width: '100px' }}
                />
              </DataTable>
            </TabPanel>

            {/* Skipped Orders Tab */}
            <TabPanel 
              header={
                <div className="flex align-items-center">
                  <i className="pi pi-minus-circle text-gray-600 mr-2" style={{marginRight: '8px'}}></i>
                  <span>Skipped</span>
                  <Badge value={executionResults.detailed_results.skipped_orders.length} className="ml-2" severity="secondary" style={{marginLeft: '8px'}}/>
                </div>
              }
            >
              <DataTable 
                value={executionResults.detailed_results.skipped_orders}
                size="small"
                stripedRows
                showGridlines
                className="p-datatable-sm"
                paginator={true}
                rows={10}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} orders"
              >
                <Column 
                  field="order_name" 
                  header="Order" 
                  body={(rowData) => (
                    <div>
                      <div className="font-semibold">{rowData.order_name}</div>
                      <div className="text-sm text-600">ID: {rowData.order_id}</div>
                    </div>
                  )}
                  style={{ minWidth: '200px' }}
                />
                <Column 
                  field="total_line_items" 
                  header="Items" 
                  style={{ width: '80px' }}
                />
                <Column 
                  field="reason" 
                  header="Reason" 
                  body={(rowData) => (
                    <div className="text-sm text-600">
                      {rowData.reason}
                    </div>
                  )}
                  style={{ minWidth: '250px' }}
                />
                <Column 
                  header="Actions" 
                  body={actionsTemplate}
                  style={{ width: '100px' }}
                />
              </DataTable>
            </TabPanel>

            {/* Failed Orders Tab */}
            <TabPanel 
              header={
                <div className="flex align-items-center">
                  <i className="pi pi-times-circle text-red-600 mr-2" style={{marginRight: '8px'}}></i>
                  <span>Failed</span>
                  <Badge value={executionResults.detailed_results.failed_orders.length} className="ml-2" severity="danger" style={{marginLeft: '8px'}}/>
                </div>
              }
            >
              <DataTable 
                value={executionResults.detailed_results.failed_orders}
                size="small"
                stripedRows
                showGridlines
                className="p-datatable-sm"
                paginator={true}
                rows={10}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} orders"
              >
                <Column 
                  field="order_name" 
                  header="Order" 
                  body={(rowData) => (
                    <div>
                      <div className="font-semibold">{rowData.order_name}</div>
                      <div className="text-sm text-600">ID: {rowData.order_id}</div>
                    </div>
                  )}
                  style={{ minWidth: '200px' }}
                />
                <Column 
                  field="total_line_items" 
                  header="Items" 
                  style={{ width: '80px' }}
                />
                <Column 
                  field="error" 
                  header="Error" 
                  body={(rowData) => (
                    <div className="text-sm text-red-600">
                      {rowData.error || 'Processing failed'}
                    </div>
                  )}
                  style={{ minWidth: '200px' }}
                />
                <Column 
                  field="order_status" 
                  header="Status" 
                  body={(rowData) => (
                    <Badge value={rowData.order_status} severity="danger" />
                  )}
                  style={{ width: '120px' }}
                />
                <Column 
                  header="Actions" 
                  body={actionsTemplate}
                  style={{ width: '100px' }}
                />
              </DataTable>
            </TabPanel>
          </TabView>
        ) : null}
      </div>

      {/* Close Button */}
      <div className="flex justify-content-center">
        <Button
          label="Close"
          icon="pi pi-times"
          onClick={onCloseDialog}
          className="p-button-primary"
          disabled={isLoading}
          autoFocus={!isLoading}
        />
      </div>

      {/* ViewOrderInfoDialog */}
      {!isLoading && (
        <ViewOrderInfoDialog
          orderSelection={orderSelection}
          lineItemsHook={lineItemsHook}
          lineItemSelection={lineItemSelection}
          visible={isOrderDialogVisible}
          onHide={() => setIsOrderDialogVisible(false)}
        />
      )}
    </>
  )
} 